import React, { useEffect, useMemo, useState } from "react";
import { QuizShowQuestion, QuizShowState } from "../pages/Games/quizShowShared";

interface MultiplayerHostDisplayProps {
  roomCode: string;
  joinUrl: string;
  questions: QuizShowQuestion[];
  state: QuizShowState | null;
  isOpen: boolean;
  send: (data: unknown) => void;
  onLeave: () => void;
}

const QUESTION_SECONDS = 20;

const MultiplayerHostDisplay: React.FC<MultiplayerHostDisplayProps> = ({
  roomCode,
  joinUrl,
  questions,
  state,
  isOpen,
  send,
  onLeave,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(QUESTION_SECONDS);
  const status = state?.status ?? "waiting";
  const questionIndex = state?.question?.index ?? 0;
  const localQuestion = questions[questionIndex];
  const players = state?.players ?? [];

  const sendQuestion = (index: number) => {
    const question = questions[index];
    if (!question) {
      send({ type: "end" });
      return;
    }
    send({
      type: "question",
      index,
      total: questions.length,
      prompt: question.prompt,
      choices: question.choices,
      correctIndex: question.correctIndex,
    });
  };

  useEffect(() => {
    if (status !== "question") {
      return;
    }
    setSecondsLeft(QUESTION_SECONDS);
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      const remaining =
        QUESTION_SECONDS - Math.floor((Date.now() - startedAt) / 1000);
      if (remaining <= 0) {
        setSecondsLeft(0);
        send({ type: "reveal" });
        window.clearInterval(intervalId);
        return;
      }
      setSecondsLeft(remaining);
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [status, questionIndex, send]);

  const rankedPlayers = useMemo(
    () => [...players].sort((left, right) => right.score - left.score),
    [players],
  );

  return (
    <div className="fixed inset-0 z-[80] flex bg-white text-black">
      <div className="flex min-w-0 flex-1 flex-col p-6">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-wide">Room code</p>
            <p className="text-5xl font-bold tracking-[0.2em]">{roomCode}</p>
            <p className="mt-2 break-all text-sm">{joinUrl}</p>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(joinUrl);
              }}
              className="mt-2 rounded border border-black bg-white px-3 py-1 text-sm font-bold hover:bg-gray-100"
            >
              Copy join link
            </button>
          </div>
          <div className="flex gap-2">
            {status === "waiting" && (
              <button
                type="button"
                onClick={() => sendQuestion(0)}
                disabled={!isOpen}
                className="rounded border border-black bg-green-500 px-4 py-2 font-bold text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Start
              </button>
            )}
            {status === "question" && (
              <button
                type="button"
                onClick={() => send({ type: "reveal" })}
                className="rounded border border-black bg-blue-500 px-4 py-2 font-bold text-white hover:bg-blue-600"
              >
                Reveal
              </button>
            )}
            {status === "reveal" && (
              <button
                type="button"
                onClick={() => sendQuestion(questionIndex + 1)}
                className="rounded border border-black bg-green-500 px-4 py-2 font-bold text-white hover:bg-green-600"
              >
                {questionIndex + 1 >= questions.length ? "Final scores" : "Next"}
              </button>
            )}
            <button
              type="button"
              onClick={onLeave}
              className="rounded border border-black bg-red-500 px-4 py-2 font-bold text-white hover:bg-red-600"
            >
              End
            </button>
          </div>
        </div>

        {!isOpen && (
          <p className="mb-4 text-red-600">Connecting to the room...</p>
        )}

        {status === "waiting" && (
          <div className="flex grow flex-col items-center justify-center text-center">
            <p className="mb-4 text-3xl font-bold">Join on your phone</p>
            <p className="max-w-xl text-xl">
              Open the join link and enter this room code. Press Start when
              everyone is in.
            </p>
          </div>
        )}

        {(status === "question" || status === "reveal") && localQuestion && (
          <div className="flex grow flex-col items-center justify-center gap-6 text-center">
            <p className="text-lg">
              Question {questionIndex + 1} of {questions.length}
              {status === "question"
                ? ` · ${secondsLeft}s · ${state?.answeredCount ?? 0}/${players.length} answered`
                : ""}
            </p>
            {localQuestion.imageUrl ? (
              <img
                src={localQuestion.imageUrl}
                alt=""
                className="max-h-[45vh] max-w-[80%] object-contain"
              />
            ) : null}
            <p className="max-w-5xl text-5xl font-bold leading-tight">
              {localQuestion.prompt}
            </p>
            {status === "reveal" && (
              <p className="text-4xl font-bold text-green-700">
                {localQuestion.choices[localQuestion.correctIndex]}
              </p>
            )}
          </div>
        )}

        {status === "ended" && (
          <div className="flex grow flex-col items-center justify-center">
            <h2 className="mb-6 text-4xl font-bold">Final scores</h2>
            <ol className="w-full max-w-md space-y-2 text-2xl">
              {rankedPlayers.map((player, index) => (
                <li key={player.id} className="flex justify-between">
                  <span>
                    {index + 1}. {player.displayName}
                  </span>
                  <span>{player.score}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <aside className="w-64 shrink-0 overflow-y-auto border-l border-black bg-gray-100 p-4">
        <h2 className="mb-4 text-xl font-bold">Players</h2>
        {rankedPlayers.length === 0 ? (
          <p>Waiting for players...</p>
        ) : (
          rankedPlayers.map((player) => (
            <div key={player.id} className="mb-2 flex justify-between">
              <span>{player.displayName}</span>
              <span>{player.score}</span>
            </div>
          ))
        )}
      </aside>
    </div>
  );
};

export default MultiplayerHostDisplay;
